import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import '@/App.css';
import LoginPage from '@/pages/LoginPage';
import FeedPage from '@/pages/FeedPage';
import ProfilePage from '@/pages/ProfilePage';
import CreatePostPage from '@/pages/CreatePostPage';
import CreateChecklistPage from '@/pages/CreateChecklistPage';
import EditChecklistPage from '@/pages/EditChecklistPage';
import MyChecklistsPage from '@/pages/MyChecklistsPage';
import SettingsPage from '@/pages/SettingsPage';
import SearchPage from '@/pages/SearchPage';
import NotificationsPage from '@/pages/NotificationsPage';
import EditProfilePage from '@/pages/EditProfilePage';
import AdminPage from '@/pages/AdminPage';
import DataDashboardPage from '@/pages/DataDashboardPage';
import MessagesPage from '@/pages/MessagesPage';
import TrendingProductsPage from '@/pages/TrendingProductsPage';
import SpotlightPage from '@/pages/SpotlightPage';
import CreateLookPage from '@/pages/CreateLookPage';
import GlowCreatorsPage from '@/pages/GlowCreatorsPage';
import BadgeRequestPage from '@/pages/BadgeRequestPage';
import GuidePage from '@/pages/GuidePage';
import QuickStartPage from '@/pages/QuickStartPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsPage from '@/pages/TermsPage';
import CreatorDashboardPage from '@/pages/CreatorDashboardPage';
import SharedChecklistPage from '@/pages/SharedChecklistPage';
import MyRoutinesPage from '@/pages/MyRoutinesPage';
import BrandProfilePage from '@/pages/BrandProfilePage';
import ShopPage from '@/pages/ShopPage';
import BrandPartnershipPage from '@/pages/BrandPartnershipPage';
import BottomNav from '@/components/BottomNav';
import { Toaster } from '@/components/ui/sonner';
import { initCapacitor } from '@/lib/capacitor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Native (Capacitor) apps serve from capacitor://localhost or https://localhost — relative /api won't hit the backend.
// On web, keep /api relative so it works across any domain (preview, production, custom).
const IS_NATIVE = typeof window !== 'undefined' && (window.Capacitor?.isNativePlatform?.() || process.env.REACT_APP_IS_NATIVE_APP === 'true');
const API = IS_NATIVE && BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// XHR-based POST helper — bypasses platform fetch interceptors that consume response bodies
function apiPost(url, data, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      try {
        const json = JSON.parse(xhr.responseText);
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: json });
      } catch (e) {
        reject(new Error(`Invalid response (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(JSON.stringify(data));
  });
}

// Auth-aware fetch wrapper — sends Bearer token from localStorage
function authFetch(url, options = {}) {
  const token = localStorage.getItem('gloverse_token');
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    if (!hash.includes('session_id=')) {
      navigate('/login');
      return;
    }

    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) {
      navigate('/login');
      return;
    }

    const processSession = async () => {
      try {
        const result = await apiPost(`${API}/auth/session`, { session_id: sessionId });
        if (!result.ok) throw new Error('Failed to authenticate');
        
        if (result.data.session_token) {
          localStorage.setItem('gloverse_token', result.data.session_token);
        }
        const userData = result.data.user || result.data;
        navigate('/', { state: { user: userData }, replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing login...</p>
      </div>
    </div>
  );
}

function GoogleAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const redirectTimerRef = useRef(null);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      console.error('Google OAuth error:', error);
      setAuthError(`Google error: ${error}`);
      redirectTimerRef.current = setTimeout(() => navigate('/login', { replace: true }), 8000);
      return;
    }

    if (!code) {
      console.error('No authorization code received');
      setAuthError('No authorization code received from Google.');
      redirectTimerRef.current = setTimeout(() => navigate('/login', { replace: true }), 8000);
      return;
    }

    // Verify state matches what we stored
    const savedState = localStorage.getItem('google_oauth_state');
    localStorage.removeItem('google_oauth_state');
    if (!savedState || savedState !== state) {
      console.error('OAuth state mismatch', { savedState: !!savedState, state: !!state });
      // Skip state check if savedState is missing (Safari private browsing fallback)
      if (!savedState) {
        console.warn('State was missing - proceeding without state verification (Safari workaround)');
      } else {
        setAuthError('Authentication state mismatch. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }
    }

    const exchangeCode = async () => {
      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const linkFlag = localStorage.getItem('google_link_mode');
        localStorage.removeItem('google_link_mode');
        const existingToken = localStorage.getItem('gloverse_token') || localStorage.getItem('session_token');
        // Only honor link mode if the user is actually logged in. Otherwise the link endpoint
        // would 401 and the user sees a misleading "Unauthorized" error on a normal login attempt.
        const isLinkMode = linkFlag && existingToken;

        if (isLinkMode) {
          const result = await apiPost(`${API}/auth/google/link`,
            { code, redirect_uri: redirectUri },
            { 'Authorization': `Bearer ${existingToken}` }
          );
          if (!result.ok) throw new Error(result.data?.detail || 'Linking failed');
          navigate('/settings', { replace: true });
          return;
        }

        // Normal login flow
        const result = await apiPost(`${API}/auth/google/exchange`, { code, redirect_uri: redirectUri });
        if (!result.ok) {
          throw new Error(result.data?.detail || `Authentication failed (${result.status})`);
        }

        if (result.data.session_token) {
          localStorage.setItem('gloverse_token', result.data.session_token);
        }
        const userData = result.data.user || result.data;
        navigate('/', { state: { user: userData }, replace: true });
      } catch (error) {
        console.error('Google auth error:', error);
        setAuthError(`Login failed: ${error.message}. Please try again.`);
        redirectTimerRef.current = setTimeout(() => navigate('/login', { replace: true }), 8000);
      }
    };

    exchangeCode();
  }, [location, navigate]);

  const handleReset = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    // Wipe all auth-related local state so the next attempt is completely fresh.
    [
      'gloverse_token',
      'session_token',
      'google_oauth_state',
      'google_link_mode',
    ].forEach(k => localStorage.removeItem(k));
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        {authError ? (
          <>
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-3" data-testid="auth-error-message">{authError}</p>
            <button
              data-testid="auth-reset-btn"
              onClick={handleReset}
              className="inline-flex items-center justify-center px-5 py-2.5 mt-2 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors shadow-sm"
            >
              Reset & try again
            </button>
            <p className="text-xs text-muted-foreground mt-4">Auto-redirecting in a few seconds…</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Completing login...</p>
          </>
        )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      const u = location.state.user;
      if (!u.profile_setup_complete && location.pathname !== '/edit-profile') {
        navigate('/edit-profile', { state: { firstTime: true }, replace: true });
      }
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
        if (!userData.profile_setup_complete && location.pathname !== '/edit-profile') {
          navigate('/edit-profile', { state: { firstTime: true }, replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('gloverse_token');
        navigate('/login');
      }
    };

    checkAuth();
  }, [location, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
}

function AppContent() {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/guide' || location.pathname === '/start' || location.pathname === '/auth/google/callback' || location.pathname.startsWith('/brand/') || location.pathname === '/shop' || location.pathname === '/partners' || location.hash.includes('session_id=');
  const hideBottomNav = isAuthPage || location.pathname.startsWith('/messages/');

  // Session time heartbeat — sends ping every 60s when user is active
  useEffect(() => {
    if (isAuthPage) return;
    const token = localStorage.getItem('gloverse_token');
    if (!token) return;

    const sendHeartbeat = () => {
      authFetch(`${API}/analytics/heartbeat`, { method: 'POST' }).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [isAuthPage]);

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/start" element={<QuickStartPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/shared/checklist/:checklistId" element={<SharedChecklistPage />} />
        <Route path="/brand/:brandName" element={<BrandProfilePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/partners" element={<BrandPartnershipPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/edit-profile" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
        <Route path="/create-post" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
        <Route path="/create-checklist" element={<ProtectedRoute><CreateChecklistPage /></ProtectedRoute>} />
        <Route path="/edit-checklist/:checklistId" element={<ProtectedRoute><EditChecklistPage /></ProtectedRoute>} />
        <Route path="/checklists" element={<ProtectedRoute><MyChecklistsPage /></ProtectedRoute>} />
        <Route path="/routines" element={<ProtectedRoute><MyRoutinesPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/messages/:conversationId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/trending" element={<ProtectedRoute><SpotlightPage /></ProtectedRoute>} />
        <Route path="/spotlight" element={<ProtectedRoute><SpotlightPage /></ProtectedRoute>} />
        <Route path="/create-look" element={<ProtectedRoute><CreateLookPage /></ProtectedRoute>} />
        <Route path="/glow-creators" element={<ProtectedRoute><GlowCreatorsPage /></ProtectedRoute>} />
        <Route path="/badge-request" element={<ProtectedRoute><BadgeRequestPage /></ProtectedRoute>} />
        <Route path="/creator-dashboard" element={<ProtectedRoute><CreatorDashboardPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DataDashboardPage /></ProtectedRoute>} />
      </Routes>
      {!hideBottomNav && <BottomNav />}
      <Toaster />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    initCapacitor(navigate);
  }, [navigate]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  if (location.pathname === '/auth/google/callback') {
    return <GoogleAuthCallback />;
  }
  
  return <AppContent />;
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL, GOOGLE_CLIENT_ID, authFetch, apiPost };
